import type { PaginatedResponse } from "@/features/cle-compliance/models/courseCertificate";
import type {
  LegalAidEvidenceListModel,
  LegalAidEvidenceModel,
  LegalAidQueryParams,
} from "@/features/cle-compliance/models/legalAid";
import { del, get, getBlob, postForm } from "./util";

export const LEGAL_AID_QUERY_KEY = ["legal-aid"] as const;

export const LEGAL_AID_LIST_QUERY_KEY = [
  ...LEGAL_AID_QUERY_KEY,
  "list",
] as const;

export const createLegalAidDetailQueryKey = (id: string) =>
  [...LEGAL_AID_QUERY_KEY, "detail", id] as const;

export const createLegalAidFileQueryKey = (id: string) =>
  [...LEGAL_AID_QUERY_KEY, "files", id] as const;

const LEGAL_AID_QUERY = "documents/legal-aid";

const createLegalAidDownloadQuery = (id: string): string =>
  `${LEGAL_AID_QUERY}/${id}/download`;

const createLegalAidsForReportingQuery = (reportId: string): string =>
  `${LEGAL_AID_QUERY}/available-for-period/${reportId}`;

const createLegalAidsForInjunctionQuery = (injunctionId: string): string =>
  `${LEGAL_AID_QUERY}/available-for-injunction/${injunctionId}`;

const createLegalAidsForImpositionQuery = (impositionId: string): string =>
  `${LEGAL_AID_QUERY}/available-for-imposition/${impositionId}`;

const createSearchParams = ({
  page,
  page_size,
  reported,
}: LegalAidQueryParams): string => {
  const searchParams = new URLSearchParams();

  if (reported != null) {
    searchParams.set("reported", String(reported));
  }

  if (page != null) {
    searchParams.set("page", String(page));
  }

  if (page_size != null) {
    searchParams.set("page_size", String(page_size));
  }

  return searchParams.toString();
};

const createListQuery = (params: LegalAidQueryParams = {}): string => {
  const queryString = createSearchParams(params);

  return queryString ? `${LEGAL_AID_QUERY}?${queryString}` : LEGAL_AID_QUERY;
};

export type CreateLegalAidRequest = {
  file: File;
  numberOfLessons: number;
};

export const fetchLegalAids = (
  params: LegalAidQueryParams = {},
): Promise<PaginatedResponse<LegalAidEvidenceListModel>> =>
  get<PaginatedResponse<LegalAidEvidenceListModel>>({
    query: createListQuery(params),
  });

export const fetchLegalAidsForReporting = (
  reportId: string,
): Promise<LegalAidEvidenceListModel[]> =>
  get<LegalAidEvidenceListModel[]>({
    query: createLegalAidsForReportingQuery(reportId),
  });

export const fetchLegalAidsForInjunction = (
  injunctionId: string,
): Promise<LegalAidEvidenceListModel[]> =>
  get<LegalAidEvidenceListModel[]>({
    query: createLegalAidsForInjunctionQuery(injunctionId),
  });

export const fetchLegalAidsForImposition = (
  impositionId: string,
): Promise<LegalAidEvidenceListModel[]> =>
  get<LegalAidEvidenceListModel[]>({
    query: createLegalAidsForImpositionQuery(impositionId),
  });

export const fetchLegalAidById = (id: string): Promise<LegalAidEvidenceModel> =>
  get<LegalAidEvidenceModel>({
    query: `${LEGAL_AID_QUERY}/${id}`,
  });

export const createLegalAid = ({
  file,
  numberOfLessons,
}: CreateLegalAidRequest): Promise<LegalAidEvidenceModel> => {
  const formData = new FormData();

  formData.append("file", file);

  return postForm<LegalAidEvidenceModel>({
    query: `${LEGAL_AID_QUERY}?numberOfLessons=${numberOfLessons}`,
    body: formData,
  });
};

export const fetchLegalAidDocumentFile = (id: string): Promise<Blob> =>
  getBlob({
    query: createLegalAidDownloadQuery(id),
  });

export const deleteLegalAid = async (id: string): Promise<void> => {
  await del({
    query: `${LEGAL_AID_QUERY}/${id}`,
  });
};
